import { useState, useCallback, useMemo } from 'react';
import { type LearnerProject } from 'LearnerProjects';
import { type StreamForDashboard, streamDashboardPath } from 'Lessons';
import { animatePathChange } from 'RouteAnimation';
import { MAX_COURSES_DISPLAYED, MAX_PROJECTS_DISPLAYED } from './StatusBox.constants';

type UseStatusBoxDisplayReturn = {
    showAllCourses: boolean;
    showAllProjects: boolean;
    displayedCourses: StreamForDashboard[];
    displayedProjects: LearnerProject[];
    hasMoreCourses: boolean;
    hasMoreProjects: boolean;
    toggleShowAllCourses: () => void;
    toggleShowAllProjects: () => void;
    handleCoursePress: (course: StreamForDashboard) => void;
};

/**
 * useStatusBoxDisplay - Custom hook for managing display state and interactions
 */
export const useStatusBoxDisplay = (
    allPastDueCourses: StreamForDashboard[],
    allPastDueProjects: LearnerProject[],
): UseStatusBoxDisplayReturn => {
    const [showAllCourses, setShowAllCourses] = useState(false);
    const [showAllProjects, setShowAllProjects] = useState(false);

    // Memoized callbacks
    const toggleShowAllCourses = useCallback(() => {
        setShowAllCourses(prev => !prev);
    }, []);

    const toggleShowAllProjects = useCallback(() => {
        setShowAllProjects(prev => !prev);
    }, []);

    const handleCoursePress = useCallback((course: StreamForDashboard) => {
        animatePathChange(streamDashboardPath({ streamTitle: course.title, streamId: course.id }));
    }, []);

    const displayData = useMemo(() => {
        const displayedCourses = showAllCourses ? allPastDueCourses : allPastDueCourses.slice(0, MAX_COURSES_DISPLAYED);
        const displayedProjects = showAllProjects
            ? allPastDueProjects
            : allPastDueProjects.slice(0, MAX_PROJECTS_DISPLAYED);
        const hasMoreCourses = allPastDueCourses.length > MAX_COURSES_DISPLAYED;
        const hasMoreProjects = allPastDueProjects.length > MAX_PROJECTS_DISPLAYED;

        return { displayedCourses, displayedProjects, hasMoreCourses, hasMoreProjects };
    }, [showAllCourses, showAllProjects, allPastDueCourses, allPastDueProjects]);

    return {
        showAllCourses,
        showAllProjects,
        displayedCourses: displayData.displayedCourses,
        displayedProjects: displayData.displayedProjects,
        hasMoreCourses: displayData.hasMoreCourses,
        hasMoreProjects: displayData.hasMoreProjects,
        toggleShowAllCourses,
        toggleShowAllProjects,
        handleCoursePress,
    };
};
